import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiGet, apiPatch } from '../services/api';
import { fmtDate } from '../services/dates';
import './EstadisticasPage.css';

const fmt  = d => fmtDate(d);
const fmtE = n => Number(n||0).toLocaleString('es-ES',{minimumFractionDigits:2,maximumFractionDigits:2})+' €';

function InlineEdit({ value, onSave, onCancel }) {
  const [v, setV] = useState(String(value||0));
  return (
    <span style={{display:'flex',gap:'.3rem',alignItems:'center'}}>
      <input type="number" min="0" step="0.01" value={v}
        onChange={e=>setV(e.target.value)}
        style={{width:'90px',padding:'.25rem .4rem',border:'1px solid var(--border)',borderRadius:'6px',fontSize:'.82rem'}}
        autoFocus
        onKeyDown={e=>{ if(e.key==='Enter') onSave(v); if(e.key==='Escape') onCancel(); }}
      />
      <button onClick={()=>onSave(v)}
        style={{padding:'.2rem .5rem',background:'var(--primary)',color:'#fff',border:'none',borderRadius:'5px',cursor:'pointer',fontSize:'.78rem'}}>✓</button>
      <button onClick={onCancel}
        style={{padding:'.2rem .5rem',background:'transparent',border:'1px solid var(--border)',borderRadius:'5px',cursor:'pointer',fontSize:'.78rem'}}>✕</button>
    </span>
  );
}

function EditableCell({ rowId, field, value, onSave, editState, setEditState }) {
  const isEditing = editState?.id === rowId && editState?.field === field;
  if (isEditing) {
    return (
      <InlineEdit
        value={value}
        onSave={v => onSave(rowId, field, v)}
        onCancel={() => setEditState(null)}
      />
    );
  }
  return (
    <span
      onClick={() => setEditState({ id: rowId, field })}
      style={{cursor:'pointer',borderBottom:'1px dashed var(--border)',padding:'.1rem 0'}}
      title="Clic para editar">
      {fmtE(value)}
    </span>
  );
}

export default function QuotesPage() {
  const { auth } = useContext(AuthContext);
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [editState, setEditState] = useState(null); // { id, field }

  async function load() {
    setLoading(true); setError('');
    try { setRows(await apiGet('/proformas/accepted', auth.token)); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line

  async function handleSave(id, field, val) {
    const num = parseFloat(val) || 0;
    const endpoint = field === 'transport_cost' ? 'transport' : 'installation';
    try {
      await apiPatch(`/proformas/${id}/${endpoint}`, { [`${field}`]: num }, auth.token);
      setRows(r => r.map(x => x.id === id ? { ...x, [field]: num } : x));
      setEditState(null);
    } catch (e) { alert(e.message); }
  }

  const totalBruto = rows.reduce((s,r) => s + Number(r.total||0), 0);
  const totalTrans = rows.reduce((s,r) => s + Number(r.transport_cost||0), 0);
  const totalInst  = rows.reduce((s,r) => s + Number(r.installation_cost||0), 0);

  if (loading) return <div className="est-loading">Cargando presupuestos aceptados…</div>;
  if (error)   return <div className="est-error">{error}</div>;

  return (
    <div className="est-page">
      <h2 style={{marginBottom:'.25rem',fontWeight:700}}>Presupuestos aceptados</h2>
      <p style={{color:'var(--muted)',fontSize:'.85rem',marginBottom:'1.25rem'}}>
        Solo se muestran los presupuestos cuyo lead ha sido marcado como ganado. Haz clic en los costes para editarlos.
      </p>

      {rows.length > 0 && (
        <div className="est-kpi-grid" style={{marginBottom:'1.25rem'}}>
          <div className="est-kpi">
            <div className="est-kpi-label">Total presupuestado</div>
            <div className="est-kpi-value">{fmtE(totalBruto)}</div>
            <div className="est-kpi-sub">{rows.length} presupuesto{rows.length!==1?'s':''}</div>
          </div>
          <div className="est-kpi">
            <div className="est-kpi-label">Coste transporte</div>
            <div className="est-kpi-value">{fmtE(totalTrans)}</div>
          </div>
          <div className="est-kpi">
            <div className="est-kpi-label">Coste instalación</div>
            <div className="est-kpi-value">{fmtE(totalInst)}</div>
          </div>
          <div className="est-kpi">
            <div className="est-kpi-label">Total + costes</div>
            <div className="est-kpi-value" style={{color:'var(--primary)'}}>{fmtE(totalBruto + totalTrans + totalInst)}</div>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="est-card">
          <div className="est-empty">
            No hay presupuestos aceptados todavía.<br/>
            Cuando un lead se marque como ganado, el presupuesto seleccionado aparecerá aquí.
          </div>
        </div>
      ) : (
        <div className="est-card est-card--no-pad">
          <div className="est-table-wrap">
            <table className="est-table">
              <thead>
                <tr>
                  <th>Lead</th>
                  <th>Empresa</th>
                  <th>Comercial</th>
                  <th>Tipo</th>
                  <th>Importe</th>
                  <th>Transporte ✎</th>
                  <th>Instalación ✎</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id}>
                    <td className="est-td-name">{r.lead_name||'—'}</td>
                    <td>{r.empresa_name||'—'}</td>
                    <td>{r.comercial_name||'—'}</td>
                    <td>
                      <span className={`est-badge ${r.tipo==='financiado'?'est-badge--blue':'est-badge--green'}`}>
                        {r.tipo==='financiado'?'Financiado':'Contado'}
                      </span>
                    </td>
                    <td>{fmtE(r.total)}</td>
                    <td>
                      <EditableCell rowId={r.id} field="transport_cost" value={r.transport_cost}
                        onSave={handleSave} editState={editState} setEditState={setEditState}/>
                    </td>
                    <td>
                      <EditableCell rowId={r.id} field="installation_cost" value={r.installation_cost}
                        onSave={handleSave} editState={editState} setEditState={setEditState}/>
                    </td>
                    <td>{fmt(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
